import { Button, Divider, Stack, StackProps, Typography, Link } from "@mui/material"
import { ErrorBlock } from "components/controls/errorInfo"
import { Loading } from "components/controls/loading"
import { Section } from "components/controls/section"
import { MailAddress } from "features/kojin/components/mailAddress"
import { useTenant } from "features/tenant/context"
import { TenantForm, useTenantForm } from "features/tenant/form"
import { TenantDetailResult, useEditTenant, useGetTenant } from "interface/tenant"
import { useState } from "react"
import { ManualIcon, OkIcon } from "shared/icons"
import { formatDateTime, safeParseDate } from "shared/util"

type EditTenantFormProps = {
  tenant: TenantDetailResult
  onEditted: (result: TenantDetailResult) => void
  onCancel: () => void
} & StackProps
const EditTenantForm = ({
  tenant,
  onEditted,
  onCancel,
  ...others
}: EditTenantFormProps) => {
  const [postState, post] = useEditTenant(tenant.tenantId, { onEditted })
  const form = useTenantForm({ tenant, onSubmit: post })
  return (
    <Stack direction="column" overflow="hidden" {...others}>
      {postState.posting && <Loading/>}
      <TenantForm {...form}/>
      <Divider/>
      {postState.error && <ErrorBlock error={postState.error}/>}
      <Stack direction="row" padding={1} spacing={1} justifyContent="flex-end">
        <Button
          onClick={form.submit}
          loading={postState.posting}
          variant="contained"
          startIcon={<OkIcon/>}
        >
          保存
        </Button>

        <Button
          onClick={onCancel}
          variant="outlined"
        >
          キャンセル
        </Button>
      </Stack>
    </Stack>
  )
}
export const ConfigureTenantPage = () => {
  const tenant = useTenant()
  const [editMode, setEditMode] = useState<'edit' | null>(null)
  const { loading, data: tenantDetail } = useGetTenant(tenant.tenantId)

  if (loading) return <Loading/>
  return (
    <Stack direction="column" overflow="auto" height="100%">
      <Section label="ドメイン">
				<Typography variant="body1">{tenant.domain}</Typography>
			</Section>

      {tenantDetail && (
        editMode === 'edit' ? (
          <EditTenantForm
            tenant={tenantDetail}
            onEditted={() => setEditMode(null)}
            onCancel={() => setEditMode(null)}
          />
        ) : (
          <Stack direction="column" overflow="hidden">
            <Section label="テナント名">
      				<Typography variant="body1">{tenantDetail.tenantName}</Typography>
            </Section>

            <Section label="リソース">
              {tenantDetail.manualURL ? (
                <Link href={tenantDetail.manualURL} target="_blank" variant="body2" underline='hover'>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ManualIcon/>
            				<Typography variant="body1">マニュアル</Typography>
                  </Stack>
                </Link>
              ) : (
         				<Typography variant="body1" color="textDisabled">なし</Typography>
              )}
            </Section>

            <Section label="メールアドレス">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body1">通知先：</Typography>
                {tenantDetail.adminAddress ? (
                  <MailAddress address={tenantDetail.adminAddress} mailStatus="OK"/>
                ) : (
           				<Typography variant="body1" color="textDisabled">なし</Typography>
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body1">送信元：</Typography>
                {tenantDetail.replyAddress ? (
                  <MailAddress address={tenantDetail.replyAddress} mailStatus="OK"/>
                ) : (
                  <Typography variant="body1" color="textDisabled">なし</Typography>
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body1">サポート：</Typography>
                {tenantDetail.supportAddress ? (
                  <MailAddress address={tenantDetail.supportAddress} mailStatus="OK"/>
                ) : (
                  <Typography variant="body1" color="textDisabled">なし</Typography>
                )}
              </Stack>
            </Section>

            {(tenantDetail.leadText || tenantDetail.signature) && (
              <Section label="メール内容">
                {tenantDetail.leadText && (
                  <Stack direction="column">
                    <Typography variant="body1">冒頭文：</Typography>
                    <Typography component="pre" variant="body1">
                      {tenantDetail.leadText}
                    </Typography>
                  </Stack>
                )}
                {tenantDetail.signature && (
                  <Stack direction="column">
                    <Typography variant="body1">署名：</Typography>
                    <Typography component="pre" variant="body1">
                      {tenantDetail.signature}
                    </Typography>
                  </Stack>
                )}
              </Section>
            )}

            {tenantDetail.effectiveTime && (
              <Section label="有効期限">
                <Typography variant="body1">{formatDateTime(safeParseDate(tenantDetail.effectiveTime) ?? null)}</Typography>
              </Section>
            )}
          </Stack>
        )
      )}
    </Stack>
  )
}
